import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Leaf, CreditCard, LogOut, Loader2, BarChart, ToggleLeft, ToggleRight, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { formatCurrency } from '../utils/formatters';
import { endOfWeek, startOfWeek, subWeeks, format, subMonths, startOfMonth, endOfMonth, subDays } from 'date-fns';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions
} from 'chart.js';

import { useAuth } from '../contexts/AuthContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export function MembershipPayments() {
  const [buckets, setBuckets] = useState<any[]>([]);
  const [selectedBucket, setSelectedBucket] = useState<string>('');
  const defaultBucketSet = React.useRef(false);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [range, setRange] = useState<'today' | 'last week' | 'this month' | 'last month' | 'custom'>('today');
  const { user, userRoles, signOut } = useAuth();
  const isAdmin = userRoles.includes('admin');
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [uniqueClients, setUniqueClients] = useState(new Set<string>());
  const [loading, setLoading] = useState(false);
  const [priorWeekSummary, setPriorWeekSummary] = useState<any>(null);
  const [currentMonthSummary, setCurrentMonthSummary] = useState<any>(null);
  const [lastMonthSummary, setLastMonthSummary] = useState<any>(null);
  const [pendingMonthSummary, setPendingMonthSummary] = useState<any>(null);
  const [currentWeekSummary, setCurrentWeekSummary] = useState<any>(null);
  const [uniqueActivePatients, setUniqueActivePatients] = useState<number | null>(null); // New state for unique active patients
  const [graphInterval, setGraphInterval] = useState<'monthly' | 'weekly'>('monthly');
  const [graphData, setGraphData] = useState<ChartData<'line'>>({
    labels: [],
    datasets: [],
  });

  const fetchGraphData = useCallback(async () => {
    if (!selectedBucket) return;

    try {
      const { data: graphData, error: graphError } = await supabase.rpc(
        'get_membership_payment_trends',
        {
          p_bucket_id: selectedBucket,
          p_interval: graphInterval,
        }
      );

      if (graphError) throw graphError;

      const now = new Date();
      const currentYearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

      // Filter out incomplete periods
      const filteredData = graphData.filter((item: any) => {
        if (graphInterval === 'monthly') {
          const [year, month] = item.period_start.split('-').map(Number);
          const itemYearMonth = `${year}-${String(month).padStart(2, '0')}`;
          return itemYearMonth !== currentYearMonth;
        } else if (graphInterval === 'weekly') {
          const periodStart = new Date(item.period_start);
          const periodEnd = new Date(item.period_end);
          return !(now >= periodStart && now <= periodEnd);
        }
        return true;
      });

      const labels = filteredData.map((item: any) => {
        const [year, month, day] = item.period_start.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return graphInterval === 'monthly'
          ? format(date, 'MMM yyyy')
          : format(date, 'MMM dd');
      });

      const amountData = filteredData.map((item: any) => item.total_amount);
      const clientsData = filteredData.map((item: any) => item.unique_clients);

      // For monthly view, append the current month's predicted Total Amount only
      let predictedAmountIndex = -1;
      if (graphInterval === 'monthly') {
        const currentMonthLabel = `${format(now, 'MMM yyyy')} (Predicted)`;
        labels.push(currentMonthLabel);

        // Predicted Total Amount: Current + Pending
        const predictedAmount = (currentMonthSummary?.total_amount || 0) + (pendingMonthSummary?.total_amount || 0);
        amountData.push(predictedAmount);

        predictedAmountIndex = labels.length - 1;
      }

      setGraphData({
        labels: labels,
        datasets: [
          {
            label: 'Total Amount',
            data: amountData,
            borderColor: 'rgb(53, 162, 235)',
            backgroundColor: 'rgba(53, 162, 235, 0.5)',
            yAxisID: 'y',
            pointStyle: 'circle',
            pointRadius: amountData.map((_, index) => (index === predictedAmountIndex ? 6 : 4)),
            pointBackgroundColor: amountData.map((_, index) => (index === predictedAmountIndex ? 'rgb(255, 165, 0)' : 'rgb(53, 162, 235)')),
            showLine: amountData.map((_, index) => index !== predictedAmountIndex),
          },
          {
            label: 'Unique Clients',
            data: clientsData,
            borderColor: 'rgb(255, 99, 132)',
            backgroundColor: 'rgba(255, 99, 132, 0.5)',
            yAxisID: 'y1',
            pointStyle: 'circle',
            pointRadius: 4,
            pointBackgroundColor: 'rgb(255, 99, 132)',
            showLine: true,
          },
        ],
      });
    } catch (error) {
      console.error('Error fetching graph data:', error);
    }
  }, [selectedBucket, graphInterval, currentMonthSummary, pendingMonthSummary, supabase]);

  useEffect(() => {
    fetchGraphData();
  }, [fetchGraphData]);

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        position: 'left',
        title: {
          display: true,
          text: 'Total Amount ($)',
        },
      },
      y1: {
        beginAtZero: true,
        position: 'right',
        title: {
          display: true,
          text: 'Unique Clients',
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('membership_payments')
        .select('membership_name, amount, payment_date, membership_patient_id')
        .order('payment_date', { ascending: false });

      let from = new Date();
      let now = new Date();

      if (range === 'today') {
        from = new Date(now.toISOString().split('T')[0]);
      } else if (range === 'last week') {
        const lastWeekEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 4 }); // Wednesday
        const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 4 });
        from = lastWeekStart;
        now = lastWeekEnd;
      } else if (range === 'this month') {
        from = startOfMonth(now);
      } else if (range === 'last month') {
        const lastMonth = subMonths(now, 1);
        from = startOfMonth(lastMonth);
        now = endOfMonth(lastMonth);
      } else if (range === 'custom' && startDate && endDate) {
        from = new Date(startDate);
        now = new Date(endDate);
      }

      query = query.gte('payment_date', from.toISOString().split('T')[0])
                 .lte('payment_date', now.toISOString().split('T')[0]);

      if (selectedBucket) {
        const { data: mappings, error: mapError } = await supabase
          .from('membership_bucket_mappings')
          .select('membership_name')
          .eq('bucket_id', selectedBucket);

        if (mapError) throw mapError;

        if (mappings) {
          const names = mappings.map(m => m.membership_name);
          query = query.in('membership_name', names);
        }
      }

      const { data, error } = await query;
      if (error) throw error;

      const grouped: { [key: string]: { payment_count: number; total_amount: number } } = {};
      const clients = new Set<string>();
      data.forEach((row: any) => {
        if (!grouped[row.membership_name]) {
          grouped[row.membership_name] = { payment_count: 0, total_amount: 0 };
        }
        grouped[row.membership_name].payment_count++;
        grouped[row.membership_name].total_amount += row.amount;
        clients.add(row.membership_patient_id);
      });
      setUniqueClients(clients);
      setData(Object.entries(grouped).map(([membership_name, stats]) => ({
        membership_name,
        ...stats
      })));

      if (selectedBucket) {
        const bucketId = selectedBucket;
      
        const { data: prior, error: priorErr } = await supabase.rpc('get_last_week_membership_payment_summary', {
          p_bucket_id: bucketId
        });
        if (!priorErr && prior?.[0]) setPriorWeekSummary(prior[0]);
      
        const { data: current, error: currentErr } = await supabase.rpc('get_current_month_membership_payment_summary', {
          p_bucket_id: bucketId
        });
        if (!currentErr && current?.[0]) setCurrentMonthSummary(current[0]);
      
        const { data: last, error: lastErr } = await supabase.rpc('get_last_month_membership_payment_summary', {
          p_bucket_id: bucketId
        });
        if (!lastErr && last?.[0]) setLastMonthSummary(last[0]);

        const { data: pending, error: pendingErr } = await supabase.rpc('get_pending_payments_due_this_month');
        if (pendingErr) throw pendingErr;
        const pendingSummaryRow = pending.find((row: any) => row.row_type === 'summary');
        if (pendingSummaryRow) setPendingMonthSummary(pendingSummaryRow);

        // Fetch current week estimated payments (summary only)
        const { data: currentWeek, error: currentWeekErr } = await supabase
          .rpc('get_payments_current_week')
          .limit(1); // Limit to 1 record since we only need the summary
        if (currentWeekErr) throw currentWeekErr;
        if (currentWeek?.[0]) setCurrentWeekSummary(currentWeek[0]);
      }

      // Fetch the total number of unique active patients
      const { data: uniquePatientsData, error: uniquePatientsErr } = await supabase
        .rpc('get_unique_active_patients');
      if (uniquePatientsErr) throw uniquePatientsErr;
      if (uniquePatientsData?.[0]) setUniqueActivePatients(uniquePatientsData[0].unique_patients);
    } catch (error: any) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [range, startDate, endDate, selectedBucket, supabase]);

  // Initial fetch for buckets
  useEffect(() => {
    const fetchBuckets = async () => {
      const { data, error } = await supabase.from('membership_buckets').select('*');
      if (!error && data) {
        setBuckets(data);
        if (!defaultBucketSet.current && data.length > 0) {
          setSelectedBucket(data[0].id);
          defaultBucketSet.current = true;
        }
      }
    };

    fetchBuckets();
  }, []);

  // Fetch data when selectedBucket changes (for summary stats and graph)
  useEffect(() => {
    if (selectedBucket) {
      fetchData();
    }
  }, [selectedBucket, fetchData]);

  // Update startDate and endDate when range changes
  useEffect(() => {
    const now = new Date();
    let from: Date;
    let to: Date;

    if (range === 'today') {
      from = now;
      to = now;
    } else if (range === 'last week') {
      const lastWeekEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 4 }); // Wednesday
      const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 4 });
      from = lastWeekStart;
      to = lastWeekEnd;
    } else if (range === 'this month') {
      from = startOfMonth(now);
      to = now;
    } else if (range === 'last month') {
      const lastMonth = subMonths(now, 1);
      from = startOfMonth(lastMonth);
      to = endOfMonth(lastMonth);
    } else {
      return; // For 'custom', don't update dates
    }

    setStartDate(format(from, 'yyyy-MM-dd'));
    setEndDate(format(to, 'yyyy-MM-dd'));
  }, [range]);

  // Handler to set range to 'custom' when date inputs are manually changed
  const handleDateChange = (setter: (value: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (range !== 'custom') {
      setRange('custom');
    }
    setter(e.target.value);
  };

  const totalAmount = useMemo(() => data.reduce((sum, item: any) => sum + item.total_amount, 0), [data]);
  const totalPayments = useMemo(() => data.reduce((sum, item: any) => sum + item.payment_count, 0), [data]);
  const uniqueClientsCount = useMemo(() => uniqueClients.size, [uniqueClients]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Leaf className="h-6 w-6 text-green-600" />
              <div className="ml-2">
                <span className="text-xl font-semibold text-gray-900">THRIVE</span>
                <span className="ml-2 text-sm text-gray-500">Membership Payments</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">{user?.email}</span>
              <div className="flex flex-wrap gap-2">
                {userRoles.map((role) => (
                  <span
                    key={role}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                  >
                    {role}
                  </span>
                ))}
              </div>
              <button
                onClick={() => signOut()}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-4 sm:px-0">
          {/* Hero Section */}
          <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-blue-600 opacity-90"></div>
              <div className="relative px-8 py-12">
                <div className="absolute left-8 top-8">
                  <button
                    onClick={() => navigate('/settings')}
                    className="text-white hover:text-green-100 transition-colors duration-200"
                  >
                    <ArrowLeft className="h-6 w-6" />
                  </button>
                </div>
                <div className="text-center">
                  <CreditCard className="h-12 w-12 text-white opacity-20 mx-auto" />
                  <h1 className="text-4xl font-bold text-white mb-2">THRIVE</h1>
                  <p className="text-green-100 text-lg mb-4">
                    Tracking Health and Reducing Illness Via Efficiency
                  </p>
                  <div className="w-16 h-1 bg-white/30 mx-auto rounded-full"></div>
                  <h2 className="text-2xl font-semibold text-white mt-4">Membership Payment Summary</h2>
                </div>
              </div>
            </div>
          </div>

          {/* Filter by Membership Category */}
          <div className="bg-white shadow rounded-lg p-6 text-gray-600 mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Membership Category:</label>
            <select
              value={selectedBucket}
              onChange={(e) => setSelectedBucket(e.target.value)}
              className="w-full border rounded px-2 py-1 text-sm"
            >
              {buckets.map(bucket => (
                <option key={bucket.id} value={bucket.id}>{bucket.name}</option>
              ))}
            </select>
          </div>

          {/* Summary Statistics */}
          <div className="bg-white shadow rounded-lg p-6 text-gray-600 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Summary</h3>
            {/* First Row: Existing 4 Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-white shadow rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700">Last Month</h4>
                <p className="text-2xl font-semibold text-green-600">
                  {lastMonthSummary ? `$${lastMonthSummary.total_amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—'}
                </p>
                <p className="text-xs text-gray-500">
                  {lastMonthSummary?.payment_count ?? 0} payments, {lastMonthSummary?.unique_clients ?? 0} clients
                </p>
              </div>
              <div className="bg-white shadow rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700">Prior 7 Days (Ending Wednesday)</h4>
                <p className="text-2xl font-semibold text-green-600">
                  {priorWeekSummary ? `$${priorWeekSummary.total_amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—'}
                </p>
                <p className="text-xs text-gray-500">
                  {priorWeekSummary?.payment_count ?? 0} payments, {priorWeekSummary?.unique_clients ?? 0} clients
                </p>
              </div>
              <div className="bg-white shadow rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700">Current Month</h4>
                <p className="text-2xl font-semibold text-green-600">
                  {currentMonthSummary ? `$${currentMonthSummary.total_amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—'}
                </p>
                <p className="text-xs text-gray-500">
                  {currentMonthSummary?.payment_count ?? 0} payments, {currentMonthSummary?.unique_clients ?? 0} clients
                </p>
              </div>
              <div className="bg-white shadow rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700">Pending Payments (May)</h4>
                <p className="text-2xl font-semibold text-green-600">
                  {pendingMonthSummary ? `$${pendingMonthSummary.total_amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—'}
                </p>
                <p className="text-xs text-gray-500">
                  {pendingMonthSummary?.total_payments ?? 0} payments, {pendingMonthSummary?.unique_patients ?? 0} patients
                </p>
              </div>
            </div>
            {/* Second Row: Existing Card + New Card */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white shadow rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700">Current Week Estimated Payments</h4>
                <p className="text-2xl font-semibold text-green-600">
                  {currentWeekSummary ? `$${currentWeekSummary.total_amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '—'}
                </p>
                <p className="text-xs text-gray-500">
                  {currentWeekSummary?.total_payments ?? 0} payments, {currentWeekSummary?.unique_patients ?? 0} patients
                </p>
              </div>
              <div className="bg-white shadow rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700">Total Active Members</h4>
                <p className="text-2xl font-semibold text-green-600">
                  {uniqueActivePatients !== null ? uniqueActivePatients.toLocaleString('en-US') : '—'}
                </p>
                <p className="text-xs text-gray-500">
                  Unique patients with active memberships
                </p>
              </div>
              {/* Placeholder for potential future card */}
            </div>
          </div>

          {/* Graph Section */}
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Membership Payment Trends</h3>
              <button
                onClick={() => setGraphInterval(graphInterval === 'monthly' ? 'weekly' : 'monthly')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                {graphInterval === 'monthly' ? 'Show Weekly' : 'Show Monthly'}
              </button>
            </div>
            <div style={{ height: '300px' }}>
              <Line options={chartOptions} data={graphData} />
            </div>
          </div>

          {/* Custom Search Range and Detailed Data */}
          <div className="bg-white shadow rounded-lg p-6 text-gray-600">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Detailed Membership Data</h3>
            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2 mb-4">
              <div className="flex items-center space-x-2 mb-2 sm:mb-0">
                <label htmlFor="dateRange" className="text-sm font-medium text-gray-700">Date Range:</label>
                <select
                  id="dateRange"
                  value={range}
                  onChange={e => setRange(e.target.value as 'today' | 'last week' | 'this month' | 'last month' | 'custom')}
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value="today">Today</option>
                  <option value="last week">Last Week</option>
                  <option value="this month">This Month</option>
                  <option value="last month">Last Month</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div className="flex items-center space-x-2 mb-2 sm:mb-0">
                <input
                  type="date"
                  className="border rounded px-2 py-1 text-sm"
                  value={startDate}
                  onChange={handleDateChange(setStartDate)}
                />
                <span className="mx-1">to</span>
                <input
                  type="date"
                  className="border rounded px-2 py-1 text-sm"
                  value={endDate}
                  onChange={handleDateChange(setEndDate)}
                />
              </div>
              <button
                onClick={() => fetchData()}
                className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="py-6 flex justify-center">
                <Loader2 className="h-6 w-6 text-gray-500 animate-spin" />
              </div>
            ) : data.length === 0 ? (
              <div className="text-center py-6 text-sm text-gray-500">No data to show. Adjust the date range and click Refresh to view data.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto text-sm">
                  <thead className="bg-gray-100 text-left">
                    <tr>
                      <th className="px-4 py-2 font-medium text-gray-700">Membership Name</th>
                      <th className="px-4 py-2 font-medium text-gray-700"># of Payments</th>
                      <th className="px-4 py-2 font-medium text-gray-700">Total Amount</th>
                    </tr>
                  </thead>
                  <tfoot>
                    <tr>
                      <td className="px-4 py-2 font-semibold text-right" colSpan={2}>Total:</td>
                      <td className="px-4 py-2 font-semibold">
                        ${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 font-semibold text-right" colSpan={2}>Total Payments:</td>
                      <td className="px-4 py-2 font-semibold">
                        {totalPayments}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2 font-semibold text-right" colSpan={2}>Unique Clients:</td>
                      <td className="px-4 py-2 font-semibold">{uniqueClientsCount}</td>
                    </tr>
                  </tfoot>
                  <tbody>
                    {data.map((item: any) => (
                      <tr key={item.membership_name}>
                        <td className="px-4 py-2">{item.membership_name}</td>
                        <td className="px-4 py-2">{item.payment_count.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                        <td className="px-4 py-2">${item.total_amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}